import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import { CLIENT_ERROR_MESSAGES } from '../errors/errorMessages';
import clientEndpoints from './clientEndpoints';

// ============================================================================
// Types
// ============================================================================

export type ViewClientStatus = 'active' | 'inactive' | 'draft';
export type ViewClientPaymentStatus = 'paid' | 'unpaid';

export interface ViewClientProfile {
  url?: string | null;
  filename?: string | null;
  isUploaded?: boolean;
  resourceId?: string;
  path?: string;
}

export interface ViewClientContactInfo {
  phoneNumber: string | null;
  countryCode: string | null;
}

export interface ViewClientAddress {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
}

export interface ViewClientDoc {
  url?: string;
  filename: string;
  isUploaded?: boolean;
  resourceId?: string;
  fileSize?: number;
  fileType?: string;
  path?: string;
}

export interface ViewClientComplianceDetails {
  nip: string | null;
  docs: ViewClientDoc[];
}

export interface ViewClientUsageLimits {
  numberOfUsers: number;
  numberOfFacilities: number;
  numberOfQmsDocuments: number;
}

export interface ViewClientSubscriptionDetails {
  subscriptionPlanId: string | null;
  planName: string | null;
  planDescription: string | null;
  durationType: string | null;
  duration: number | null;
  billingCycle: string | null;
  price: string | null;
  usageLimits: ViewClientUsageLimits | null;
  planStatus: 'active' | 'inactive' | null;
  paymentStatus: ViewClientPaymentStatus | null;
  nextRenewalDate: string | null;
}

export interface ViewClientInvoiceAddress {
  addressLine1: string | null;
  addressLine2: string | null;
  zipCode: string | null;
  city: string | null;
  province: string | null;
}

export interface ViewClientBillingDetails {
  companyName: string | null;
  nip: string | null;
  regon: string | null;
  krs: string | null;
  invoiceAddress: ViewClientInvoiceAddress | null;
}

export interface ViewClient {
  id: string;
  clientId: string | null;
  name: string;
  email: string;
  role: string | null;
  status: ViewClientStatus;
  currency: string | null;
  tenantId: string | null;
  contactInfo: ViewClientContactInfo | null;
  address: ViewClientAddress | null;
  profileUrl: ViewClientProfile | null;
  complianceDetails: ViewClientComplianceDetails | null;
  subscriptionDetails: ViewClientSubscriptionDetails | null;
  /** Dane do faktury — top-level company/tax data plus the invoice mailing address. */
  billingDetails: ViewClientBillingDetails | null;
  subscriptionTransactionHistory: unknown[];
  createdAt: string;
  updatedAt: string;
}

export interface ViewClientApiResponse {
  client: ViewClient;
}

export interface ViewClientResult {
  success: boolean;
  data?: ViewClientApiResponse;
  error?: string;
}

// ============================================================================
// Helpers
// ============================================================================

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const msg = apiError.message || apiError.error;
    if (msg && msg !== 'Internal Server Error') {
      return msg;
    }
    if (msg === 'Internal Server Error') {
      return CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
    }
    return CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  return CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
};

// ============================================================================
// API
// ============================================================================

/**
 * Fetches a single client's details by clientId (UUID).
 * Endpoint: GET loopay-client-management/clients/{clientId}
 */
export const viewClient = async (
  clientId: string
): Promise<ViewClientResult> => {
  if (!clientId?.trim()) {
    return { success: false, error: CLIENT_ERROR_MESSAGES.DEFAULT_ERROR };
  }

  try {
    const result = await apiService.getAPI(
      clientEndpoints.VIEW_CLIENT(clientId.trim())
    );

    if (!result.error) {
      return { success: true, data: result.data as ViewClientApiResponse };
    }

    if (result.isNetworkError) {
      return { success: false, error: CLIENT_ERROR_MESSAGES.NETWORK_ERROR };
    }

    return { success: false, error: extractErrorMessage(result.data) };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response && !!error.request) {
        return { success: false, error: CLIENT_ERROR_MESSAGES.NETWORK_ERROR };
      }
      return {
        success: false,
        error: extractErrorMessage(error.response?.data),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
